import { useEffect, useState, useCallback } from "react";
import { Menu, X, ChevronRight, Sun, Moon, Languages } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'motion/react';
import { usePrefs } from '../../hooks/hooks.prefs';

const NAV_LINKS = [
    { id: 'services',   fr: "Services",   en: "Services",   href: "/#services",  sectionId: "services" },
    { id: 'stack',      fr: "Stack",      en: "Stack",      href: "/#techstack", sectionId: "techstack" },
    { id: 'projects',   fr: "Projets",    en: "Work",       href: "/#projects",  sectionId: "projects" },
    { id: 'about',      fr: "À propos",   en: "About",      href: "/#about",     sectionId: "about" },
    { id: 'playground', fr: "Playground", en: "Playground", href: "/playground",  sectionId: null },
    { id: 'contact',    fr: "Contact",    en: "Contact",    href: "/contact",    sectionId: null },
];

// pages dont le haut est sombre : la barre est transparente avec un texte clair
const DARK_TOP = ['/', '/playground', '/contact'];

export function ThemeToggle({ onDark, className = '' }) {
    const { theme, toggleTheme, t } = usePrefs();
    const dark = theme === 'dark';
    return (
        <button
            onClick={toggleTheme}
            aria-label={dark ? t('Passer en mode clair', 'Switch to light mode') : t('Passer en mode sombre', 'Switch to dark mode')}
            title={dark ? t('Mode clair', 'Light mode') : t('Mode sombre', 'Dark mode')}
            className={`relative w-10 h-10 inline-flex items-center justify-center rounded-xl border transition-colors ${onDark ? 'border-white/15 text-slate-200 hover:bg-white/10' : 'border-duna-border text-duna-muted hover:text-duna-text hover:bg-duna-pill'} ${className}`}
        >
            <motion.span key={theme} initial={{ rotate: -90, opacity: 0, scale: 0.6 }} animate={{ rotate: 0, opacity: 1, scale: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 18 }}>
                {dark ? <Moon size={17} /> : <Sun size={17} />}
            </motion.span>
        </button>
    );
}

export function LangToggle({ onDark, className = '' }) {
    const { lang, setLang, t } = usePrefs();
    return (
        <div role="group" aria-label={t('Langue', 'Language')} className={`inline-flex items-center h-10 p-1 rounded-xl border ${onDark ? 'border-white/15' : 'border-duna-border'} ${className}`}>
            <Languages size={14} aria-hidden="true" className={`mx-1.5 ${onDark ? 'text-slate-400' : 'text-duna-subtle'}`} />
            {['fr', 'en'].map((l) => (
                <button
                    key={l}
                    onClick={() => setLang(l)}
                    aria-pressed={lang === l}
                    className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${
                        lang === l
                            ? onDark ? 'bg-white/15 text-white' : 'bg-duna-text text-duna-bg'
                            : onDark ? 'text-slate-400 hover:text-white' : 'text-duna-muted hover:text-duna-text'
                    }`}
                >
                    {l}
                </button>
            ))}
        </div>
    );
}

export default function ViewHeader() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const location = useLocation();
    const navigate = useNavigate();
    const { t, tx } = usePrefs();
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.3 });

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
            for (const id of ['home', 'services', 'techstack', 'projects', 'about']) {
                const el = document.getElementById(id);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    if (rect.top <= 100 && rect.bottom > 100) {
                        setActiveSection(id);
                        break;
                    }
                }
            }
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => { setIsOpen(false); }, [location.pathname]);

    const handleNavClick = useCallback((e, link) => {
        e.preventDefault();
        setIsOpen(false);
        if (link.sectionId) {
            if (location.pathname !== '/') {
                navigate('/');
                setTimeout(() => document.getElementById(link.sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
            } else {
                document.getElementById(link.sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else if (link.href === '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            if (location.pathname !== '/') navigate('/');
        } else {
            navigate(link.href);
        }
    }, [location.pathname, navigate]);

    const onDark = !scrolled && DARK_TOP.includes(location.pathname);

    return (
        <>
            {/* barre de progression de lecture */}
            <motion.div aria-hidden="true" className="fixed top-0 left-0 right-0 h-0.5 z-[210] origin-left bg-gradient-to-r from-teal-400 via-sky-400 to-violet-500" style={{ scaleX: progress }} />

            <nav className={`fixed top-0 left-0 right-0 z-[200] transition-all duration-300 ${scrolled ? 'py-2.5' : 'py-4'}`} aria-label={t('Navigation principale', 'Main navigation')}>
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <div className={`flex justify-between items-center gap-3 rounded-2xl px-3 sm:px-4 h-14 transition-all duration-300 ${
                        scrolled ? 'bg-duna-surface/80 backdrop-blur-xl border border-duna-border shadow-lg shadow-black/5' : 'border border-transparent'
                    }`}>
                        <a href="/" onClick={(e) => handleNavClick(e, { href: '/', sectionId: null })} className="flex items-center gap-2.5 no-underline group shrink-0">
                            <img src="/assets/images/logo.png" alt="" width="32" height="32" className="w-8 h-8 object-contain transition-transform duration-300 group-hover:rotate-[-8deg] group-hover:scale-110" />
                            <span className={`font-bold text-base tracking-tight ${onDark ? 'text-white' : 'text-duna-text'}`}>
                                Savan<span className={onDark ? 'text-teal-300' : 'text-duna-accent'}>Tech</span>
                            </span>
                        </a>

                        <div className="hidden lg:flex items-center gap-0.5">
                            {NAV_LINKS.map(link => {
                                const isActive = link.sectionId ? location.pathname === '/' && activeSection === link.sectionId : location.pathname === link.href;
                                return (
                                    <a
                                        key={link.id}
                                        href={link.href}
                                        onClick={(e) => handleNavClick(e, link)}
                                        aria-current={isActive ? 'page' : undefined}
                                        className={`relative px-3.5 py-2 text-sm font-medium rounded-lg no-underline transition-colors ${
                                            isActive
                                                ? onDark ? 'text-white' : 'text-duna-text'
                                                : onDark ? 'text-slate-300 hover:text-white' : 'text-duna-muted hover:text-duna-text'
                                        }`}
                                    >
                                        {isActive && (
                                            <motion.span layoutId="nav-pill" className={`absolute inset-0 rounded-lg -z-0 ${onDark ? 'bg-white/10' : 'bg-duna-pill'}`} transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                                        )}
                                        <span className="relative">{tx(link)}</span>
                                    </a>
                                );
                            })}
                        </div>

                        <div className="hidden lg:flex items-center gap-2">
                            <LangToggle onDark={onDark} />
                            <ThemeToggle onDark={onDark} />
                            <Link
                                to="/contact"
                                className={`group flex items-center gap-1.5 h-10 px-4 text-sm font-semibold rounded-xl no-underline transition-all ${onDark ? 'bg-teal-400 text-slate-950 hover:bg-teal-300' : 'bg-duna-text text-duna-bg hover:opacity-85'}`}
                            >
                                {t('Démarrer un projet', 'Start a project')}
                                <ChevronRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                            </Link>
                        </div>

                        <div className="flex lg:hidden items-center gap-1.5">
                            <ThemeToggle onDark={onDark} />
                            <button
                                onClick={() => setIsOpen(v => !v)}
                                aria-expanded={isOpen}
                                aria-label={isOpen ? t('Fermer le menu', 'Close menu') : t('Ouvrir le menu', 'Open menu')}
                                className={`w-11 h-11 inline-flex items-center justify-center rounded-xl transition-colors ${onDark ? 'text-white hover:bg-white/10' : 'text-duna-text hover:bg-duna-pill'}`}
                            >
                                {isOpen ? <X size={22} /> : <Menu size={22} />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* ── Menu mobile ── */}
            <div className={`lg:hidden fixed inset-0 z-[190] transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
                <div className={`absolute top-0 left-0 right-0 bg-duna-surface border-b border-duna-border shadow-2xl pt-[84px] pb-6 px-4 rounded-b-3xl transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : '-translate-y-full'}`}>
                    <div className="flex flex-col gap-1">
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.id}
                                href={link.href}
                                onClick={(e) => handleNavClick(e, link)}
                                className="flex items-center justify-between text-base font-medium no-underline py-3 px-4 rounded-xl text-duna-text hover:bg-duna-pill"
                            >
                                {tx(link)}
                                <ChevronRight size={18} className="text-duna-subtle" />
                            </a>
                        ))}
                        <div className="mt-3 flex items-center justify-between px-1">
                            <LangToggle />
                            <ThemeToggle />
                        </div>
                        <Link
                            to="/contact"
                            onClick={() => setIsOpen(false)}
                            className="mt-4 py-3.5 bg-duna-text text-duna-bg text-center rounded-xl font-semibold no-underline text-sm flex items-center justify-center"
                        >
                            {t('Démarrer un projet', 'Start a project')}
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
}
